import { useState, useRef, useEffect } from 'react';
import { Upload, Send, X, User, MapPin, Shield, ExternalLink } from 'lucide-react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { User as FirebaseUser } from 'firebase/auth';
import { db, appId } from '@/lib/firebase';
import { compressImage, ImageMetadata } from '@/utils/imageCompression';
import { ReportCategory, REPORT_CATEGORIES } from '@/types/reports';

interface SubmitModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: FirebaseUser | null;
  userProfile: { displayName: string; photoUrl?: string | null } | null;
  showNotify: (message: string, type?: 'success' | 'error' | 'info') => void;
}

export const SubmitModal = ({
  isOpen,
  onClose,
  user,
  userProfile,
  showNotify,
}: SubmitModalProps) => {
  const [loading, setLoading] = useState(false);
  const [reportCategory, setReportCategory] = useState<ReportCategory>('ANTI_EPAL');
  const [formData, setFormData] = useState({
    politician: '',
    location: '',
    description: '',
    image: null as string | null,
    // DILG Anti-Epal Drive fields (for ANTI_EPAL category)
    isGovernmentFunded: false,
    projectType: 'government_project' as 'government_project' | 'government_program' | 'government_activity' | 'government_property',
    projectName: '',
    fundingSource: 'local' as 'national' | 'local' | 'both',
    violationType: 'name' as 'name' | 'image' | 'likeness' | 'combination',
    // Additional fields for other categories
    expectedTimeline: '',
    serviceType: '',
    complianceType: '',
  });
  const [preview, setPreview] = useState<string | null>(null);
  const [imageMetadata, setImageMetadata] = useState<ImageMetadata | null>(null);
  const [geoLocation, setGeoLocation] = useState<{ lat: number; lng: number; accuracy?: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get current category configuration
  const categoryConfig = REPORT_CATEGORIES[reportCategory];

  // Request geolocation on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGeoLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (error) => {
          console.log('[SubmitModal] Geolocation not available:', error.message);
        },
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 }
      );
    }
  }, []);

  // Focus management and keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    // Prevent body scroll when modal is open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Handle Escape key to close modal
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setReportCategory('ANTI_EPAL');
      setFormData({
        politician: '',
        location: '',
        description: '',
        image: null,
        isGovernmentFunded: false,
        projectType: 'government_project',
        projectName: '',
        fundingSource: 'local',
        violationType: 'name',
        expectedTimeline: '',
        serviceType: '',
        complianceType: '',
      });
      setPreview(null);
      setImageMetadata(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showNotify('File too large. Please upload an image under 5MB.', 'error');
        return;
      }
      setLoading(true);
      try {
        const { base64, metadata } = await compressImage(file);
        setFormData((prev) => ({ ...prev, image: base64 }));
        setPreview(base64);
        setImageMetadata(metadata);
      } catch (err) {
        console.error('[SubmitModal] Failed to process image:', err);
        showNotify('Failed to process image.', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userProfile) {
      showNotify('Please create a profile to submit evidence-based reports.', 'error');
      return;
    }
    if (!formData.image || !formData.location) {
      showNotify('Please provide an image and location.', 'error');
      return;
    }
    
    // Validate required fields based on category
    const requiredFields = categoryConfig.required;
    if (requiredFields.includes('politician') && !formData.politician) {
      showNotify('Please provide the politician name.', 'error');
      return;
    }
    if (requiredFields.includes('isGovernmentFunded') && !formData.isGovernmentFunded) {
      showNotify('Please confirm this is a government-funded project to comply with DILG Anti-Epal Drive policy.', 'error');
      return;
    }
    if (requiredFields.includes('projectName') && !formData.projectName) {
      showNotify('Please provide the project name.', 'error');
      return;
    }

    setLoading(true);
    try {
      const reportData: any = {
        location: formData.location,
        description: formData.description || null,
        imageUrl: formData.image,
        userId: user?.uid,
        authorName: userProfile.displayName,
        authorPhoto: userProfile.photoUrl || null,
        createdAt: serverTimestamp(),
        likes: 0,
        // Phase 2: Report category
        reportCategory: reportCategory,
        // Moderation fields
        status: 'pending',
        isPublic: false,
        // Reporter snapshot
        reporter: {
          userId: user?.uid || '',
          displayName: userProfile.displayName,
          photoUrl: userProfile.photoUrl || null,
        },
        // Geolocation (if available)
        geo: geoLocation || null,
        // Image metadata
        imageMeta: imageMetadata || null,
        // Community signals (initialize to 0)
        communitySignals: {
          validVotes: 0,
          abuseFlags: 0,
          commentsCount: 0,
        },
      };

      // Add category-specific fields
      if (reportCategory === 'ANTI_EPAL') {
        reportData.politician = formData.politician;
        reportData.isGovernmentFunded = formData.isGovernmentFunded;
        reportData.projectType = formData.projectType;
        reportData.projectName = formData.projectName || null;
        reportData.fundingSource = formData.fundingSource;
        reportData.violationType = formData.violationType;
      } else {
        // For other categories, add relevant fields
        if (categoryConfig.visible.includes('projectName')) {
          reportData.projectName = formData.projectName || null;
        }
        if (categoryConfig.visible.includes('expectedTimeline')) {
          reportData.expectedTimeline = formData.expectedTimeline || null;
        }
        if (categoryConfig.visible.includes('serviceType')) {
          reportData.serviceType = formData.serviceType || null;
        }
        if (categoryConfig.visible.includes('complianceType')) {
          reportData.complianceType = formData.complianceType || null;
        }
      }

      await addDoc(
        collection(db, 'artifacts', appId, 'public', 'data', 'reports'),
        reportData
      );
      onClose();
      setReportCategory('ANTI_EPAL');
      setFormData({ 
        politician: '', 
        location: '', 
        description: '', 
        image: null,
        isGovernmentFunded: false,
        projectType: 'government_project',
        projectName: '',
        fundingSource: 'local',
        violationType: 'name',
        expectedTimeline: '',
        serviceType: '',
        complianceType: '',
      });
      setPreview(null);
      setImageMetadata(null);
      showNotify('Citizen report submitted successfully! It will be reviewed by moderators.', 'success');
    } catch (error) {
      console.error('[SubmitModal] Failed to submit report:', error);
      showNotify('Failed to submit report.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="submit-modal-title"
      onClick={(e) => {
        // Close modal when clicking backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center flex-shrink-0">
          <h2 
            id="submit-modal-title"
            className="font-bold text-white text-xl"
          >
            Submit Evidence-Based Report
          </h2>
          <button
            onClick={onClose}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClose();
              }
            }}
            className="text-white/80 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/50 rounded-lg p-1.5 transition-colors"
            aria-label="Close report submission modal"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {!userProfile ? (
          <div className="p-8 text-center overflow-y-auto">
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-blue-600">
              <User size={32} />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Profile Required</h3>
            <p className="text-gray-500 mb-6">
              To maintain accountability, please set up a verified profile before
              submitting an evidence-based report.
            </p>
            <button
              onClick={onClose}
              className="w-full bg-blue-600 text-white font-bold py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Profile
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="overflow-y-auto flex-1">
            <div className="p-6 space-y-5">
              {/* Phase 2: Category Selection */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Report Category *
                </label>
                <select
                  required
                  value={reportCategory}
                  onChange={(e) => {
                    const newCategory = e.target.value as ReportCategory;
                    setReportCategory(newCategory);
                    // Reset form fields when category changes
                    setFormData({
                      ...formData,
                      politician: newCategory === 'ANTI_EPAL' ? formData.politician : '',
                      isGovernmentFunded: false,
                    });
                  }}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                >
                  <option value="ANTI_EPAL">Anti-Epal Watch</option>
                  <option value="PROJECT_DELAY">Project Delay</option>
                  <option value="GHOST_PROJECT">Ghost Project</option>
                  <option value="SERVICE_FAILURE">Service Failure</option>
                  <option value="COMPLIANCE_GAP">Compliance Gap</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {categoryConfig.description}
                </p>
              </div>

              <div
                onClick={() => fileInputRef.current?.click()}
                className={`relative border-2 border-dashed rounded-xl h-56 flex flex-col items-center justify-center cursor-pointer transition-all ${
                  preview
                    ? 'border-blue-500 bg-blue-50/30'
                    : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/20'
                }`}
              >
                {preview ? (
                  <>
                    <img
                      src={preview}
                      alt="Evidence preview"
                      className="h-full w-full object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors rounded-lg flex items-center justify-center">
                      <div className="bg-white/90 rounded-full p-2 opacity-0 hover:opacity-100 transition-opacity">
                        <Upload size={20} className="text-blue-600" />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center p-6">
                    <div className="bg-blue-100 p-4 rounded-full w-fit mx-auto mb-3 text-blue-600">
                      <Upload size={28} />
                    </div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">
                      Click to upload evidence
                    </p>
                    <p className="text-xs text-gray-500">
                      Photos of signage, banners, vehicles (Max 5MB)
                    </p>
                  </div>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  className="hidden"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Politician Name - Only for ANTI_EPAL category */}
                {categoryConfig.visible.includes('politician') && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-2">
                      Politician Name {categoryConfig.required.includes('politician') && '*'}
                    </label>
                    <div className="relative">
                      <User
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      />
                      <input
                        required={categoryConfig.required.includes('politician')}
                        type="text"
                        className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                        value={formData.politician}
                        onChange={(e) =>
                          setFormData({ ...formData, politician: e.target.value })
                        }
                        placeholder="e.g. Mayor Juan"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">
                    Location *
                  </label>
                  <div className="relative">
                    <MapPin
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      required
                      type="text"
                      className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                      value={formData.location}
                      onChange={(e) =>
                        setFormData({ ...formData, location: e.target.value })
                      }
                      placeholder="e.g. Brgy Hall"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  rows={3}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all text-sm"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Describe your observation in detail (e.g., location, context, what you observed)..."
                />
              </div>

              {/* DILG Anti-Epal Drive Compliance Section - Only for ANTI_EPAL category */}
              {reportCategory === 'ANTI_EPAL' && (
              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-2 border-blue-200 rounded-xl p-5 space-y-4 shadow-sm">
                <div className="flex items-center gap-2.5 mb-1">
                  <div className="bg-blue-600 p-1.5 rounded-lg">
                    <Shield size={16} className="text-white" />
                  </div>
                  <h3 className="font-bold text-blue-900 text-base">
                    DILG Anti-Epal Policy Compliance
                  </h3>
                </div>
                
                {/* Government Funded Verification */}
                <label className="flex items-start gap-3 cursor-pointer bg-white rounded-lg p-3 border border-blue-200 hover:border-blue-300 transition-colors">
                  <input
                    type="checkbox"
                    required
                    checked={formData.isGovernmentFunded}
                    onChange={(e) => setFormData({ ...formData, isGovernmentFunded: e.target.checked })}
                    className="mt-0.5 w-4 h-4 flex-shrink-0 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-xs text-blue-900 leading-relaxed">
                    <strong className="block mb-1">I confirm this is a government-funded project/program/activity/property</strong>
                    This report documents observed promotional materials related to{' '}
                    <a 
                      href="https://dilg.gov.ph/news/ANTI-EPAL-DRIVE-DILG-Orders-Immediate-Removal-of-Officials-Names-and-Images-from-Government-Projects/NC-2026-1047"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-700 underline hover:text-blue-900 inline-flex items-center gap-1 font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      DILG Memorandum NC-2026-1047
                      <ExternalLink size={11} />
                    </a>
                    {' '}(Anti-Epal Drive)
                  </span>
                </label>

                <div className="grid grid-cols-2 gap-3">
                  {/* Project Type */}
                  <div>
                    <label className="block text-xs font-semibold text-blue-900 mb-1.5">
                      Project Type *
                    </label>
                    <select
                      required
                      value={formData.projectType}
                      onChange={(e) => setFormData({ ...formData, projectType: e.target.value as any })}
                      className="w-full px-2.5 py-2 bg-white border border-blue-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    >
                      <option value="government_project">Government Project</option>
                      <option value="government_program">Government Program</option>
                      <option value="government_activity">Government Activity</option>
                      <option value="government_property">Government Property</option>
                    </select>
                  </div>

                  {/* Funding Source */}
                  <div>
                    <label className="block text-xs font-semibold text-blue-900 mb-1.5">
                      Funding Source *
                    </label>
                    <select
                      required
                      value={formData.fundingSource}
                      onChange={(e) => setFormData({ ...formData, fundingSource: e.target.value as any })}
                      className="w-full px-2.5 py-2 bg-white border border-blue-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    >
                      <option value="local">Local (LGU)</option>
                      <option value="national">National</option>
                      <option value="both">Both</option>
                    </select>
                  </div>
                </div>

                {/* Observed Material Type */}
                <div>
                  <label className="block text-xs font-semibold text-blue-900 mb-1.5">
                    Type of Observed Material *
                  </label>
                  <select
                    required
                    value={formData.violationType}
                    onChange={(e) => setFormData({ ...formData, violationType: e.target.value as any })}
                    className="w-full px-2.5 py-2 bg-white border border-blue-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  >
                    <option value="name">Official&rsquo;s Name</option>
                    <option value="image">Official&rsquo;s Image/Photo</option>
                    <option value="likeness">Official&rsquo;s Likeness</option>
                    <option value="combination">Combination (Name + Image/Likeness)</option>
                  </select>
                </div>

                {/* Project Name */}
                <div>
                  <label className="block text-xs font-semibold text-blue-900 mb-1.5">
                    Project/Program Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.projectName}
                    onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
                    placeholder="e.g., Barangay Health Center, Road Construction"
                    className="w-full px-2.5 py-2 bg-white border border-blue-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>

                {/* Legal Disclaimer */}
                <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-3 text-xs text-amber-900 leading-relaxed">
                  <strong className="font-semibold">Important Notice:</strong> This portal documents observed promotional materials related to DILG Memorandum 
                  NC-2026-1047 (Anti-Epal Drive) on <strong>government-funded projects only</strong>. 
                  Reports must be factual and supported by photographic evidence. 
                  VerifyPH documents citizen-submitted evidence and does not make legal judgments or official determinations.
                </div>
              </div>
              )}

              {/* Additional fields for other categories */}
              {reportCategory !== 'ANTI_EPAL' && categoryConfig.visible.includes('projectName') && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">
                    Project Name {categoryConfig.required.includes('projectName') && '*'}
                  </label>
                  <input
                    required={categoryConfig.required.includes('projectName')}
                    type="text"
                    value={formData.projectName}
                    onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
                    placeholder="e.g., Barangay Health Center, Road Construction"
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                  />
                </div>
              )}

              {categoryConfig.visible.includes('expectedTimeline') && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">
                    Expected Timeline
                  </label>
                  <input
                    type="text"
                    value={formData.expectedTimeline}
                    onChange={(e) => setFormData({ ...formData, expectedTimeline: e.target.value })}
                    placeholder="e.g., Q1 2024, December 2023"
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                  />
                </div>
              )}

              {categoryConfig.visible.includes('serviceType') && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">
                    Service Type
                  </label>
                  <input
                    type="text"
                    value={formData.serviceType}
                    onChange={(e) => setFormData({ ...formData, serviceType: e.target.value })}
                    placeholder="e.g., Health services, Education, Transportation"
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                  />
                </div>
              )}

              {categoryConfig.visible.includes('complianceType') && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">
                    Compliance Type
                  </label>
                  <input
                    type="text"
                    value={formData.complianceType}
                    onChange={(e) => setFormData({ ...formData, complianceType: e.target.value })}
                    placeholder="e.g., Building code, Environmental, Safety standards"
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                  />
                </div>
              )}

              <div className="sticky bottom-0 bg-white pt-4 -mx-6 px-6 pb-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={loading || !formData.image || !formData.isGovernmentFunded}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white font-bold py-3.5 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 text-base"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Submit Evidence-Based Report
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
